import {
  formatDistanceStrict,
  format,
  parseISO,
  differenceInCalendarDays,
  fromUnixTime,
  subDays,
  getUnixTime,
  add,
  subSeconds,
  getTime,
  set,
} from 'date-fns';
import { Map, List } from 'immutable';
import { constants as rootCauseConstants } from '@saucelabs/sl-module-root-cause';

import { FILTERS_MAP } from './ducks/filters/constants';
import * as urlQueryString from './urlQueryString';
import * as constants from './constants';
import { formatDuration as formatTime } from './time';

const ONE_DAY = 24 * 60 * 60 * 1000;
const MAX_DAYS = 30;
const { OWNER_FILTER_SCOPES } = rootCauseConstants;

export const getStartEndDate = (dateFilter, base = new Date()) => {
  const type = dateFilter.get('type');
  const value = dateFilter.get('value');
  const startDate = type === 'absolute' ? new Date(value.startDate.getTime()) : new Date(base - (value * 1000));
  const endDate = type === 'absolute' ? new Date(value.endDate.getTime()) : new Date(base);

  return {
    startDate,
    endDate,
  };
};

export const formatDuration = (selectedDateFilter, includeStartDay = false) => {
  const { startDate, endDate } = getStartEndDate(selectedDateFilter);
  const startDateModified = includeStartDay ? subDays(startDate, 1) : startDate;
  return formatDistanceStrict(startDateModified, endDate);
};

export const getFiltersFromQueryParams = (allQueryParams, defaultFilters = {}) => (
  Object.keys(allQueryParams)
    .reduce((ac, key) => ({
      ...ac,
      ...(FILTERS_MAP.has(key) && { [key]: allQueryParams[key] }),
    }), defaultFilters)
);

export const getDateFilterFromQueryParams = (allQueryParams) => {
  let dateValue = JSON.parse(allQueryParams.dateFilter);
  if (dateValue.type === 'absolute') {
    dateValue = {
      ...dateValue,
      value: {
        startDate: new Date(dateValue.value.startDate),
        endDate: new Date(dateValue.value.endDate),
      },
    };
  }
  return Map(dateValue);
};

export const selectedDateTransformer = (location, payload) => (
  urlQueryString.set(location, { dateFilter: JSON.stringify(payload) })
);

export const selectedOwnerTransformer = (location, payload) => (
  urlQueryString.set(location, { [constants.ROOTCAUSE_QUERY_STRINGS.USER]: payload.first() })
);

export const removeFilterTransformer = (location, payload) => (
  urlQueryString.remove(location, payload.name, (
    payload.name === constants.OWNER_FILTER_KEYS.OWNERS ? payload.value.name : payload.value
  ))
);

export const selectFilterTransformer = (location, payload) => (
  urlQueryString.set(location, {
    [payload.name]: payload.name === constants.OWNER_FILTER_KEYS.OWNERS ?
      payload.items.map(({ name }) => name).toJSON() :
      payload.items.toJSON(),
  })
);

export const searchValueTransformer = (location, payload) => (
  urlQueryString.set(location, { query: payload })
);


export const clearSelectedFilterTransformer = (location) => {
  const allQueryParams = urlQueryString.getAll(location);
  return Object.keys(allQueryParams)
    .reduce((ac, param) => ({
      ...ac,
      ...(!FILTERS_MAP.get(param) && { [param]: allQueryParams[param] }),
    }), {});
};

export const notifyNewError = (nextErrors, prevErrors, callback) => {
  if (nextErrors.equals(prevErrors)) {
    return;
  }
  nextErrors.forEach((value, key) => {
    if (!value || prevErrors.get(key) === value) return;
    callback(`There was an error: ${value}.`);
  });
};

export class NoDataError extends Error {}

export const calculatePercentage = (partialValue, totalValue, decimal = 2) => (
  totalValue ? (100 * (partialValue || 0)) / totalValue : 0
).toFixed(decimal);

export const formatLabel = (item) => (item || 0);

export const formatComparisonLabel = (item, date) => {
  const pastData = formatLabel(item.get('previousValue'));
  const currentData = formatLabel(item.get('value'));
  if (typeof currentData === 'string') return null;

  // right now we only store ~30 days of test data.  If the selected time range is greater
  // than 16 days we will not have accurate data to compare.
  if (date.get('value') >= constants.SIXTEEN_DAYS) {
    return '--';
  }

  if (currentData === pastData) {
    return `vs. ${pastData} (0%)`;
  }

  //  if the prior number of tests ran is equal to 0 it's not possible to
  //  accurately calculate the percent
  if (pastData === 0) {
    return `vs. ${pastData} (--%)`;
  }

  const percentage = (((currentData - pastData) / pastData) * 100).toFixed(1);
  return `vs. ${pastData} (${percentage}%)`;
};

// method to get date interval for api call (minute / hour / day)
export const getInterval = (timeSpan) => {
  if (timeSpan > 60 * 60 * 24) {
    return '1d';
  }

  if (timeSpan <= 60 * 60) {
    return '1m';
  }

  return '1h';
};

export const prepareTrendsTestsData = ({ buckets, start, end }) => {
  let totalTests = 0;
  let totalPassedTests = 0;
  const testsCountData = [];
  const statusCountData = [];
  const startDate = start * 1000;
  const endDate = end * 1000;

  if (!buckets.length) {
    return {
      totalTests,
      totalPassedTests,
      testsCountData,
      statusCountData,
    };
  }

  const prefixMissingTime = buckets[0].timestamp - startDate;
  const prefixMissingDays = prefixMissingTime ? Math.ceil(prefixMissingTime / ONE_DAY) : 0;
  const suffixMissingTime = endDate - buckets[buckets.length - 1].timestamp;
  const suffixMissingDays = suffixMissingTime ? Math.ceil(suffixMissingTime / ONE_DAY) : 0;

  // Add pre missing days
  if (prefixMissingDays) {
    for (let count = 0; count < prefixMissingDays; count += 1) {
      const timestamp = startDate + (ONE_DAY * count);
      testsCountData.push({ timestamp });
      statusCountData.push({ timestamp });
    }
  }

  buckets.forEach(({ timestamp, count, aggs: { status } }) => {
    const statusData = status.reduce((acc, info) => ({
      ...acc,
      [info.name]: info.count,
    }), {});
    totalTests += count || 0;
    totalPassedTests += statusData.passed || 0;
    testsCountData.push({ timestamp, count });
    statusCountData.push({ timestamp, ...statusData });
  });

  // Add post missing days
  if (suffixMissingDays) {
    for (let count = suffixMissingDays - 1; count >= 0; count -= 1) {
      const timestamp = endDate - (ONE_DAY * count);
      testsCountData.push({ timestamp });
      statusCountData.push({ timestamp });
    }
  }

  return {
    totalTests,
    totalPassedTests,
    testsCountData,
    statusCountData,
  };
};

// Paralellization efficiency percentage
// Some inaccuracy may be present because 'duration_test_max' and 'duration_absolute'
// are calculated using a formula 'session_end_time - start_time', while other fields
// using a formula 'end_time - start_time'.
// Thus, using <= instead of === and Math.max.
//
// 1 is returned if a build run in parallel, i.e. its duration is equal to the duration
// if the longest test. In other words, when duration is closer to duration_absolute,
// then efficiency is closer to 0.
export const buildEfficiency = (build) => (
  (build.duration_absolute - build.duration_test_max) <= 0 ?
    1 :
    Math.max(1 - (
      (build.duration - build.duration_test_max) /
      (build.duration_absolute - build.duration_test_max)
    ), 0)
);

export const buildStatus = ({ aggs: { status } }) => {
  if (status.length === 1) {
    return status[0].name;
  }

  if (status.find(({ name }) => name === constants.STATUSES.errored.value)) {
    return constants.STATUSES.errored.value;
  }

  if (status.find(({ name }) => name === constants.STATUSES.failed.value)) {
    return constants.STATUSES.failed.value;
  }

  return constants.STATUSES.complete.value;
};

export const prepareBuildsData = (builds) => (
  !Array.isArray(builds) ? [] : (
    builds.map((buildInfo) => ({
      ...buildInfo,
      tests: List(buildInfo.tests),
      efficiency: buildEfficiency(buildInfo),
      status: buildStatus(buildInfo),
    }))
  )
);

export const calculateEfficiency = (value) => {
  if (value >= 0.90) {
    return `parallelized ${(value * 100).toFixed(0)}%`;
  } if (value < 0.1) {
    return `sequential ${(value * 100).toFixed(0)}%`;
  }

  return `semi-parallel (${(value * 100).toFixed(0)}%)`;
};
export const calulateTotalTestCount = (testData) => (
  testData.reduce((prev, curr) => prev + (curr.count || 0), 0)
);

export const calculatePassedPercentage = (statusCountData, testsCountData) => {
  const testCount = calulateTotalTestCount(testsCountData);
  const statusCount = statusCountData.reduce((prev, curr) => (
    prev + (curr.passed || 0)
  ), 0);

  return testCount === 0 ?
    0 :
    ((statusCount / testCount) * 100).toFixed(0);
};

export const formatToPercent = (tick) => `${(tick * 100).toFixed(2)}%`;

export const formatTestDetailValue = (key, value) => {
  switch (key) {
    case constants.TESTS_TABLE_COLUMNS.startTime.key:
      return value ? format(
        parseISO(value),
        constants.TEST_START_TIME_FORMAT,
      ) : '';
    case constants.TESTS_TABLE_COLUMNS.duration.key:
      return formatTime(value);
    case constants.BUILDS_TABLE_COLUMNS.efficiency.key:
      return calculateEfficiency(value);
    default:
      return value;
  }
};

export const getChartSummary = ({ testsCountData, statusCountData }) => (
  statusCountData ?
    `${calculatePassedPercentage(statusCountData, testsCountData)}% passed` :
    `${calulateTotalTestCount(testsCountData)} tests`
);

export const getSelectedOwner = (selectedOwners, item) => {
  const isSelected = selectedOwners.has(item);

  if (constants.OWNER_RADIO_OPTIONS.includes(item.name) && !isSelected) {
    return selectedOwners
      .filter((({ name }) => !constants.OWNER_RADIO_OPTIONS.includes(name)))
      .add(item);
  }

  return isSelected ?
    selectedOwners.remove(item) :
    selectedOwners.add(item);
};

export const prepareOwnersQuery = (owners) => {
  const scope = owners.find(({ name }) => constants.OWNER_RADIO_OPTIONS.includes(name));
  const ownerTeams = owners
    .filter(({ type }) => type === constants.OWNER_FILTER_KEYS.TEAM)
    .map(({ name }) => name);
  const ownerUsers = owners
    .filter(({ type }) => type === constants.OWNER_FILTER_KEYS.USER)
    .map(({ name }) => name);

  return {
    ...(scope?.name && ({ scope: scope.name })),
    ...(ownerTeams.length && ({ [constants.OWNER_FILTER_KEYS.TEAM]: ownerTeams })),
    ...(ownerUsers.length && ({ [constants.OWNER_FILTER_KEYS.USER]: ownerUsers })),
  };
};

export const formatActivityData = (data) => (
  data.size > 1 ? Array.from(data.entries()).reduce((acc, [user, activity]) => {
    activity.forEach((entry) => {
      const index = acc.findIndex(({ datestamp2 }) => (
        datestamp2 === entry.datestamp2
      ));
      if (index < 0) {
        acc.push({
          name: entry.name,
          datestamp2: entry.datestamp2,
          [`tests_by_day2_${user}`]: entry.tests_by_day2,
          [`peak_vm_ccy2_${user}`]: entry.peak_vm_ccy2,
        });
      } else {
        acc[index] = {
          ...acc[index],
          [`tests_by_day2_${user}`]: entry.tests_by_day2,
          [`peak_vm_ccy2_${user}`]: entry.peak_vm_ccy2,
        };
      }
    });
    return acc;
  }, []) :
    data.valueSeq().first()
);

export const formatDataKeys = ({ dataPrefix, activity, colors }) => (
  Array.from(activity.keys()).map((key, index) => ({
    name: `${dataPrefix}_${key}`,
    color: colors[index],
  }))
);

export const prepareActivityRequests = ({
  user,
  scope,
  since,
  until,
  owners,
  owner_indv: users = [],
  owner_team: teams = [],
  dateFilterType,
}) => {
  const orgId = user.get('organization').id;
  const username = user.get('username');
  const teamId = user.getUserTeamId();
  const isOrgAdmin = user.isOrgAdmin();
  const defaultParams = {
    since,
    until,
    orgId,
    dateFilterType,
  };

  const activityRequestsData = [];
  if (scope) {
    if (scope === OWNER_FILTER_SCOPES.ORGANIZATION) {
      activityRequestsData.push({
        ...defaultParams,
        isXTMAdmin: isOrgAdmin,
        username: isOrgAdmin ? 'organization' : username,
        type: isOrgAdmin ?
          constants.ACTIVITY_REQUEST_TYPES.SCOPE_ORG_XTM :
          constants.ACTIVITY_REQUEST_TYPES.SCOPE_ORG_USER,
      });
    }

    if (scope === OWNER_FILTER_SCOPES.MYSELF) {
      activityRequestsData.push({
        ...defaultParams,
        username,
        type: constants.ACTIVITY_REQUEST_TYPES.SCOPE_MYSELF,
      });
    }

    if (scope === OWNER_FILTER_SCOPES.TEAM) {
      activityRequestsData.push({
        ...defaultParams,
        teamId,
        username: user.getUserTeamName(),
        type: constants.ACTIVITY_REQUEST_TYPES.SCOPE_MYTEAM,
      });
    }
  }

  users.forEach((selectedUserName) => {
    activityRequestsData.push({
      ...defaultParams,
      username: selectedUserName,
      type: constants.ACTIVITY_REQUEST_TYPES.OWNERS_USER,
    });
  });

  teams.forEach((team) => {
    const teamInfo = owners.findEntry(({ type, name }) => (
      type === constants.OWNER_FILTER_KEYS.TEAM && name === team),
    );

    if (teamInfo?.length) {
      activityRequestsData.push({
        ...defaultParams,
        teamId: team,
        username: teamInfo[0].label,
        type: constants.ACTIVITY_REQUEST_TYPES.OWNERS_TEAM,
      });
    }
  });

  return activityRequestsData;
};

export const setTempJobOwner = (user, selectedOwner) => (
  user.isAdmin() && selectedOwner ? selectedOwner : user.username
);

/**
 * Method used to double selected time period for API calls
 * used to compare current user data over a selected numver of days
 * with the prior same numer of days prior
 * @param {Object[]} dates
 * @param {Date} dates[].since - begining date
 * @param {Date} dates[].until - end date
 * @param {boolean} includeSince - if should include since day in difference
 * @returns {number} - time in MS
 */
export const xDaysAgo = ({ since, until }, includeSince = constants.INCLUDE_START_IN_ACTIVITY) => {
  const sinceDate = fromUnixTime(since);
  const untilDate = fromUnixTime(until);

  const doubledDiff = differenceInCalendarDays(untilDate, sinceDate) * 2;

  return getUnixTime(
    subDays(
      untilDate,
      doubledDiff + (includeSince ? 1 : 0),
    ),
  );
};

export const parseJSON = (stringifiedJSON) => {
  let parsedJSON = {};
  try {
    parsedJSON = JSON.parse(stringifiedJSON);
  } catch (error) {
    parsedJSON = {};
  }
  return parsedJSON;
};

/**
 * @param {Immutable.Map|Immutable.Record} metrics
 * @param {string} selectedMetric
 * @returns {number}
 */
export const calculateHealthPercentage = (metrics, selectedMetric) => {
  const totalTestCases = metrics.getIn(['totalCasesLimited', 'value']);
  const selected = metrics.getIn([selectedMetric, 'value']);
  return totalTestCases > 0 ? (selected / totalTestCases) * 100 : 0;
};

/**
 * @param {Immutable.Map|Immutable.Record} metrics
 * @param {string} valueKey
 * @returns {number}
 */
export const calculateMixedResults = (metrics, valueKey) => {
  const totalCasesLimited = metrics.getIn(['totalCasesLimited', valueKey]);
  return Object.values(constants.TEST_PAGE_HEALTH_SNAPSHOT_LEGEND).reduce((acc, { status }) => (
    acc - metrics.getIn([status, valueKey])
  ), totalCasesLimited);
};

/**
 * @param {string} interval
 * @param {string} dateTime
 * @returns {{start: number, end: number}} range object
 */
export const getTimeRangeFromInterval = (interval, dateTime) => {
  const start = getUnixTime(new Date(dateTime));
  const [, value, unit] = interval.match(/(\d+)(\w)/) || [];

  if (!value && !unit) {
    console.warn('Invalid interval passed!'); // eslint-disable-line no-console

    return {
      start,
      end: start,
    };
  }

  const endDate = new Date(dateTime);
  const end = getUnixTime(
    subSeconds(
      add(endDate, {
        days: (unit === 'd') ? value : 0,
        hours: (unit === 'h') ? value : 0,
        minutes: (unit === 'm') ? value : 0,
      }),
      1,
    ),
  );

  return {
    start,
    end,
  };
};

/**
 * @param {number} start start date timestamp
 * @param {number} end end date timestamp
 * @returns {boolean}
 */
export const isDateRangeValid = (start, end) => {
  if ((!start || !end) || (start > end)) {
    return false;
  }

  const now = set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  const limit = getTime(subDays(now, MAX_DAYS));

  if (start <= limit) {
    return false;
  }

  return true;
};
